// 2026-09-02 신규 — 등급 화면 공용 뷰모델(대표 확정, 「등급 화면 카탈로그」 열세 상태).
//
// 홈 카드(데스크톱·모바일) · 상단바 배지 툴팁 · /settings 등급 카드 · /my-tier 히어로가 **같은
// 상태 판정과 같은 문장**을 쓰게 하려고 한 곳에 모았다. 화면 컴포넌트는 여기서 나온 뷰모델을
// 그리기만 한다 — 문장을 각 화면에서 다시 짓지 않는다. 카탈로그 목업이 데이터 객체 하나에서
// 네 화면을 그려냈던 구조를 그대로 코드로 옮긴 것이다.
//
// 상태(kind) 13종과 판정 순서 — 순서가 중요하다(앞이 이기는 규칙):
//   dormant       정식 등급 휴면 (is_dormant && 정식 등급 보유)
//   rookieDormant 루키 휴면
//   stage0        승인됐고 채널 없음 (등급 행 없음)
//   stage1        채널 있음, 작품 영상 0 (등급 행 없거나 rookie 0/5)
//   rookie        루키 진행 중 1~4편
//   review        루키 5/5 · 승급 심사 중 (2026-09-06 대표 결정 — 5편을 채우면 레이블리가 영상을 확인한 뒤 실버 확정)
//   reviewRejected 루키 5/5 · 심사 반려 → 보완 뒤 다시 신청 (반려 사유는 어드민이 쓴 문장을 그대로 `note` 로)
//   debut         이번 달 루키 → 첫 정식 등급 (1~7일, 또는 승인일 `debutedAt` 로부터 7일 — 승인은 달 중간에도 난다)
//   promoted      승급 직후 (1~7일)
//   demoted       강등 직후 (1~7일)
//   up            오늘 기준이 적용 등급보다 위
//   down          오늘 기준이 적용 등급보다 아래
//   top           마스터
//   stable        그 외 정식 등급
//
// 원칙(카탈로그 02절):
//   - 방향은 문장으로 — "↑" 대신 "이대로면 10월 1일 다이아몬드", "↓" 대신 "지금 추이면 10월 1일 골드".
//     예측 문구는 대표 결정으로 **한 달 내내** 켠다. "예상"이 아니라 조건문("지금 추이면")을 쓴다.
//   - 내려감도 말한다, 부끄럽지 않게 — 강등은 색 없이 짙은 칩 + 앞을 보는 문장("돌아가려면 +N점"), 7일만.
//   - 휴면은 하나의 등급 상태다(대표 결정, 만료 규칙 없음). 루키 휴면도 "휴면" 배지를 단다.
//   - 마지막 활동은 "마지막 쇼츠 제작일"(youtube_video_map.published_at 최댓값) — 새 컬럼 없음.
//   - 날짜는 nextConfirmDateLabel() 에서만. 여기서 문자열로 만들지 않는다.
//   - 문장은 고정 사전에서만 고른다(MyTier.md 🔒 — 개인화 서술 생성 금지).
//
// 강조는 세그먼트 배열로 돌려준다: [{ text, strong }] — 화면이 <b>로 그린다.
import { nextConfirmDateLabel, monthLabel, nextTierName } from "./creatorTierMetrics";
import { ROOKIE_LABEL, DORMANT_LABEL, ROOKIE_VIDEO_THRESHOLD, CREATOR_TIER_NAMES, MASTER_TIER_NAME, MASTER_TOP_RATIO, tierIndex } from "./creatorTierNames";
import { tt, ttl } from "../i18n/tt";
import { fmtMonthDay, fmtYearMonthDay } from "./formatLocale";
import { segsFromMarked } from "./markedSegs";
import { TIER_SATISFACTION_IN_SCORE, isLinearNow } from "./tierSatisfaction";

// 승급·강등·데뷔 배지 표시 기간 — 그 달의 1~7일. 한 달 내내 띄우면 축하(또는 경고)가 아니라 배경이 된다.
export const RECENT_CHANGE_DAYS = 7;

export const TIER_VIEW_KINDS = [
  "dormant", "rookieDormant", "stage0", "stage1", "rookie", "rookieFilled", "review", "reviewRejected", "reviewApproved", "rookieStay",
  "debut", "promoted", "demoted", "up", "down", "top", "stable",
];
// 루키 화면(루키 라벨 · 진행 점)을 쓰는 종류 — buildTierView 와 내 등급 화면(MyTier ROOKIE_SCREEN)이 같은 목록을 본다(113차 #5)
export const ROOKIE_LIKE_KINDS = ["rookieDormant", "stage0", "stage1", "rookie", "rookieFilled", "review", "reviewRejected", "reviewApproved", "rookieStay"];

const seg = (text, strong = false) => ({ text, strong });
const plain = (text) => [seg(text)];

function withinRecentChangeWindow(now) {
  return now.getDate() <= RECENT_CHANGE_DAYS;
}

// 승인일(`debutedAt`)로부터 RECENT_CHANGE_DAYS 안인지 — 달 중간 승인도 데뷔 배지를 받게(2026-09-06).
//   날짜(달력) 기준 — 자정에 다시 그리면(useTodayKey) 그날 창이 닫힌다. 시각 차(ms)로 세면 자정 뒤에도 몇 시간 더 남았다(130차 #2)
function withinDaysOf(iso, now, days = RECENT_CHANGE_DAYS) {
  const diff = calendarDaysBetween(iso, now);
  return diff !== null && diff >= 0 && diff < days;
}

// 지금부터 거꾸로 N×24시간 안인지 — 서버가 interval 로 세는 창과 맞출 때
function withinRollingDays(iso, now, days) {
  if (!iso) return false;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  // 기기 시계가 서버보다 조금(15분 안) 늦어 방금 일어난 일이 「미래」로 보여도 창 안으로 본다(135차 #2).
  // 허용 폭을 작게 둔다 — 늦은 시계는 창 끝도 그만큼 늦게 닫으므로 서버 감시 창보다 최대 15분만 더 남는다(136차 #3)
  const diff = now.getTime() - d.getTime();
  return diff >= -CLOCK_SKEW_MS && diff < days * 86400000;   // [열림, 닫힘) — 아래 rollingWindowNextBoundary 와 같은 경계(141차 #3)
}

const CLOCK_SKEW_MS = 15 * 60 * 1000;

// 다음에 창이 열리거나 닫히는 시각(ms) — 화면이 그 시각에 다시 그리도록(useTierView · 135차 #1).
// 기기 시계가 15분 넘게 늦어 아직 「미래」인 값이면 열리는 때(값 − 15분)를, 창 안이면 닫히는 때를 준다(138차 #3). 지났거나 없으면 null
function rollingWindowNextBoundary(iso, days, now) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  const opens = t - CLOCK_SKEW_MS;
  const closes = t + days * 86400000;
  const n = now.getTime();
  return n < opens ? opens : n < closes ? closes : null;
}

// 두 시각 사이의 달력 일수(그 기기의 날짜 기준 · 서머타임과 무관하게 UTC 자정끼리 뺀다). 날짜를 못 읽으면 null
function calendarDaysBetween(iso, now) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const a = Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
  const b = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((b - a) / 86400000);
}

// 2026-10-09 — 자동 종료(lapsed) 뒤 7일 안이면 심사 행의 크리에이터 문장(왜 진행도가 내려갔는지). 루키·0편(stage1)·채널 없음(stage0)·휴면 루키(rookieDormant) 카드가 같이 쓴다(리뷰 8차 #6 · 28차 #5 · 36차 #3 · 50차 #1)
function lapsedNote(summary, now) {
  const r = summary?.rookieReview;
  // 서버 문장은 쓰지 않으므로(129차 #3) 문장이 비어 있는 옛 행·손으로 닫은 행도 까닭을 보인다(130차 #7)
  //   7일은 서버의 감시 창(rookie_review_watch_row · reviewed_at >= now() - 7일 — 168시간)과 같게. 달력 7일로 세면 서버가 아직 감시 중인데 안내가 먼저 사라졌다(134차 #6)
  if (!(r?.status === "lapsed" && withinRollingDays(r?.reviewedAt, now, 7))) return null;
  // 서버 문장은 「다시 채우면 심사가 자동으로 다시 열려요」로 끝난다 — 「루키로 남기」를 골랐거나 심사 제도가 꺼져 있으면 서버가 다시 열지 않으므로(rookie_promotion_review_due)
  // 그 약속을 하지 않는 문장으로 바꾼다(77차 #1)
  // 심사 제도가 꺼져 있으면 그 사실이 먼저 — 「실버로 가기」 선택 카드는 제도가 꺼지면 보이지 않고, 바꿔도 다시 열리지 않는다(84차 #2)
  //   심사 제도가 꺼져 있으면 목표를 채운 회원은 다음 등급 결정일에 정식 등급이 된다(종전 흐름 · app_config.rookie_review_enabled) — 카드 본문이 그 길을 말하고, 여기는 까닭만(130차 #6 · 132차 #5 — 같은 문장 한 곳)
  if (summary.rookieStay && summary.rookieReviewEnabled !== false) return tt("a.tier.lapsedStay", "올린 작품 영상 중 삭제되거나 비공개된 영상이 있어 승급 심사가 자동으로 종료됐어요. 「루키로 남기」를 고르셔서 영상을 다시 채워도 심사가 자동으로 열리지 않아요 — 실버로 가고 싶으면 선택을 「실버로 가기」로 바꿔 주세요.");
  // 서버 문장은 「다시 채우면 심사가 자동으로 다시 열려요」를 약속한다 — 반려 뒤 같은 영상만 되살리는 등 다시 열리지 않는 경우가 있어 카드는 종료 까닭만 말한다(129차 #3 · 알림 문구 자체는 대표 승인 항목)
  return tt("a.tier.lapsedOnly", "올린 작품 영상 중 삭제되거나 비공개된 영상이 있어 승급 심사가 자동으로 종료됐어요.");
}

// 2026-09-06 — 등급 요약에 승급 심사 상태(useRookiePromotionStatus)를 얹는다. 훅 결과가 없으면 요약 그대로.
// `rookieReview`(pending/approved/rejected 행) · `rookieEligible`(5/5 루키) · `debutedAt`(승인 시각) 세 필드만 추가.
export function withRookieReview(summary, rookieStatus) {
  if (!summary || !rookieStatus) return summary;
  const review = rookieStatus.review || null;
  // 진행도는 **일별 계산**(rookieStatus)이 정본이다. summary.rookieProgress 는 월 확정 행이라 1일에 얼어 있어,
  // 달 중간에 올린 영상이 반영되지 않는다(9/1 확정 4/5 · 오늘 실제 5/5 → 카드가 「한 편만 더」로 거짓말).
  const live = Number.isFinite(rookieStatus.progressCurrent)
    ? { current: rookieStatus.progressCurrent, target: rookieStatus.progressTarget || ROOKIE_VIDEO_THRESHOLD }
    : null;
  return {
    ...summary,
    ...(live ? { rookieProgress: live } : null),
    rookieReview: review,
    rookieEligible: Boolean(rookieStatus.eligible),
    // 2026-09-06 밤 — 루키 작품만 세기(rookie_practice_only). 루키 문장이 「루키 작품 영상」으로 바뀐다.
    rookiePracticeOnly: Boolean(rookieStatus.practiceOnly),
    // 2026-10-02 「루키로 남기」(대표 결정) — 본인이 골랐으면 5/5 여도 심사가 열리지 않는다(set_rookie_stay)
    rookieStay: Boolean(rookieStatus.stay),
    // 심사 제도 스위치(전체) — 자동 종료 뒤 다시 여는 서버 판정(rookie_promotion_review_due)과 같은 값. 회원 규칙까지 곱한 enabled 를 쓰면
    // 기존 회원(규칙 적용 전)이 「심사를 멈췄다」로 잘못 읽힌다(77차 #1 · 78차 #2). 모르면(옛 서버) null
    rookieReviewEnabled: typeof rookieStatus.reviewSwitch === "boolean" ? rookieStatus.reviewSwitch : null,
    debutedAt: review && review.status === "approved" ? review.reviewedAt || null : null,
  };
}

// "5월 12일" / "2025년 12월 23일" — 마지막 쇼츠 제작일 표기. 연도는 지난해 이전일 때만.
export function shortsDateLabel(iso, now = new Date()) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const m = d.getMonth() + 1;
  const day = d.getDate();
  return d.getFullYear() === now.getFullYear() ? fmtMonthDay(m, day) : fmtYearMonthDay(d.getFullYear(), m, day);
}

// 달력 일수 — 「마지막 쇼츠 10/8 · 3일 전」의 날짜와 일수가 같은 기준이게(130차 #2)
export function daysSince(iso, now = new Date()) {
  const diff = calendarDaysBetween(iso, now);
  return diff === null ? null : Math.max(0, diff);
}

// 휴면 N개월째 — 마지막 쇼츠 제작일로부터. 90일 미만이면 휴면이 아니므로 최소 3개월.
export function dormantMonthsLabel(days) {
  if (days === null || days === undefined) return null;
  const months = Math.max(1, Math.floor(days / 30));
  return tt("a.7c7725a0", "{{v0}} {{v1}}개월째", { v0: DORMANT_LABEL, v1: months });
}

// summary → kind. 훅이 주는 필드만 본다(컷 수치 등 노출 금지 값은 훅이 애초에 내보내지 않는다).
export function resolveTierViewKind(summary, now = new Date()) {
  if (!summary) return null;
  const recent = withinRecentChangeWindow(now);
  const isRookie = summary.status === "rookie";
  const isPre = summary.status === "pre-rookie";
  if (summary.isDormant) return isRookie || isPre || !summary.primaryTier ? "rookieDormant" : "dormant";
  // 2026-10-06 — 월 확정 행이 없는 pre-rookie 도 일별 진행도(훅 `rookieProgress`)·심사 정보가 있으면 루키와
  // 같은 분기로 본다. 그 전에는 1일까지 무조건 stage1(0/5)로 그려 달 중간에 5편을 채운 회원이 「작품 하나를
  // 골라 신청부터」 안내를 받았다. 진행도·심사가 전혀 없을 때만 stage0/1.
  // 오늘 일별 계산이 이미 정식이면(적힌 진행도 0 이어도 — 기존 회원 등) 루키 갈래의 「오늘 기준 ○○」 카드로(126차 #1 · stage0/1 「사용 신청부터」가 아니다)
  const preHasProgress = isPre && ((summary.rookieProgress?.current ?? 0) > 0 || Boolean(summary.rookieReview) || summary.rookieEligible === true || Boolean(summary.formalDailyTier));
  if (isPre && !preHasProgress) return summary.hasChannels ? "stage1" : "stage0";
  if (isRookie || preHasProgress) {
    const cur = summary.rookieProgress?.current ?? 0;
    // 2026-09-06 — 승급 심사(대표 결정). 5/5 인데 심사 행이 아직 없어도(스캔 전) 대상이면 「심사 중」으로 본다.
    const target = summary.rookieProgress?.target ?? ROOKIE_VIDEO_THRESHOLD;
    const review = summary.rookieReview || null;
    // 스캔(07:20) 전이라 심사 행이 없어도 서버가 자격을 인정하면(rookieEligible, 일별 계산 5/5) 「심사 중」으로 본다.
    // cur 은 월 확정값이라 달 중간에 5편을 채운 사람은 아직 4/5 로 남아 있다 — 그 값으로 막으면 「한 편만 더」가 뜬다.
    // 자격은 서버(rookieEligible)가 정본. 훅 결과가 아예 없을 때(undefined — RPC 실패·비활성)만 진행도로 대신 판단한다.
    // rookieEligible === false 는 「심사 대상 아님」이라는 명시적 답이므로 존중한다.
    // 2026-10-06 밤 — 「자격 알 수 없음 + 5/5 → 심사 중」 예비 판정은 **월 확정 루키**에게만. pre-rookie 는 일별 진행도만
    // 있는 상태라 RPC 응답 전(또는 실패)에 5/5 만으로 「승급 심사 중」이 떴다(이성재 사례 — 기존 회원은 11/1 까지 심사 대상이 아님).
    // pre-rookie 의 「심사 중」은 서버가 자격(rookieEligible=true)을 주거나 심사 행(pending)이 있을 때만.
    // 2026-10-11 — 그 예비 판정(자격 모름 + 5/5 → 「심사 중」)을 뺐다(121차 #3): 심사 상태를 못 읽었으면 심사 제도 꺼짐·「루키로 남기」·승인을 알 수 없어
    // 「승급 심사 중」은 짐작이다. 상태를 모르면 5편 채움 카드가 채운 사실만 말한다(120차 #1 · 월 행 없는 회원과 같은 규칙)
    // 2026-10-02 — 본인 취소(withdrawn, 「루키로 남기」)는 심사 행이 없는 것과 같다. 루키로 남기를 골랐으면 5/5 여도 「심사 중」이 아니라 「루키로 활동 중」.
    // 2026-10-09 — 자동 종료(lapsed, 영상 삭제·비공개로 5편 미만)도 같다. 진행도가 내려갔으니 「루키 N/5」 카드로 돌아간다.
    const active = review && review.status !== "withdrawn" && review.status !== "lapsed" ? review : null;
    // 승인된 심사 — 실버로 확정됐다. 「루키로 남기」 기록이 남아 있어도 승인이 먼저다(113차 #3).
    // 이 갈래(월 확정 루키 · 일별 진행도만 있는 pre-rookie)는 훅이 다음 등급·간격·게이지를 계산하지 않으므로 데뷔 카드를 그리지 않고
    // 늘 「승급 승인」 카드(113차 #1 — 데뷔 카드는 「최고 등급 유지 중」·「null까지」를 그렸다). 정식 월 행이 생기면 아래 정식 갈래가 데뷔를 그린다
    if (active?.status === "approved") return "reviewApproved";
    // 진행 중인 심사(대기·반려)가 없는데 오늘 일별 계산이 이미 정식 등급이면(승인 · 기존 회원 · 심사 꺼짐) 「○편 더 올리면」을 말하지 않는다 —
    // 5편 채움 카드가 「오늘 기준 ○○ 등급으로 계산됐어요」(123차 #1·#2 · 적힌 진행도는 점 칸에 그대로 · 122차 #7).
    // 심사 상태를 읽은 뒤에도 같다(124차 #1 — 예전엔 상태를 못 읽었을 때만이라 읽고 나면 「한 편만 더」로 뒤집혔다)
    if (!active && summary.formalDailyTier) return "rookieFilled";
    // 심사 제도 스위치가 꺼져 있으면(서버가 알려 준 값이 false) 심사 행 없이 자격만으로 「심사 중」이라 하지 않는다 — 서버가 열지 않는다(rookie_promotion_review_due ·
    // 자동 종료 뒤 5/5 로 돌아와도 「승급 심사 중」이 끝없이 떴다 · 110차 #1). 그때는 루키 카드가 「심사 꺼짐(종전 흐름)」을 말한다
    const reviewOpenable = summary.rookieReviewEnabled !== false;
    // 「루키로 남기」 카드는 「실버로 가기는 아래 카드에서」를 안내한다 — 심사가 멈춘 동안은 그 선택 카드가 보이지 않고 바꿔도 열리지 않으므로
    // 「심사 꺼짐(종전 흐름)」(5편 채움 카드)이 먼저(118차 #1 · 자동 종료 안내 lapsedNote 와 같은 순서)
    if (reviewOpenable && summary.rookieStay && active?.status !== "pending" && (summary.rookieEligible === true || (isRookie && cur >= target))) return "rookieStay";
    if (active?.status === "pending" || (!active && reviewOpenable && summary.rookieEligible === true)) return "review";
    // 반려 카드는 「다시 신청」으로 심사 길을 안내한다 — 심사 제도가 꺼져 있으면 서버가 신청을 거절하고(22023) 종전 흐름(다음 결정일)으로 가므로 채움·루키 카드로(132차 #2)
    if (active?.status === "rejected" && reviewOpenable) return "reviewRejected";
    // 5편을 채웠는데 심사 중·승인·반려·루키로 남기가 아닌 상태 — 따로 이름을 둔다(112차 #3 · 「0편 더 올리면」 대신 지금 상태를 말한다)
    if (cur >= target) return "rookieFilled";
    // 0편이고 채널도 없으면(자동 종료 뒤 채널을 다 지운 회원 등) 다음 할 일은 채널 등록 — 「작품 골라 신청」이 아니다(리뷰 50차 #1)
    if (cur <= 0) return summary.hasChannels === false ? "stage0" : "stage1";
    return "rookie";
  }
  if (!summary.primaryTier) return null;
  if ((recent && summary.debuted) || withinDaysOf(summary.debutedAt, now)) return "debut";
  if (recent && summary.promotedFrom) return "promoted";
  if (recent && summary.demotedFrom) return "demoted";
  if (summary.direction === "up") return "up";
  if (summary.direction === "down") return "down";
  if (!summary.nextTier) return "top";
  return "stable";
}

const ROOKIE_STEPS = (channelDone, videoText, videoNow) => [
  { text: tt("a.c164f7de", "채널 등록"), state: channelDone ? "done" : "now" },
  { text: videoText, state: videoNow ? "now" : "" },
  { text: tt("a.34d5db6b", "정식 등급"), state: "" },
];

// 남은 편수 문구 — 1편이면 "한 편만 더".
function remainText(remaining) {
  return remaining === 1 ? tt("a.0bddbf05", "한 편만 더") : tt("a.7dc35e17", "{{v0}}편 남음", { v0: remaining });
}

/**
 * 등급 화면 공용 뷰모델.
 * @param {object|null} summary useCreatorTierSummary 결과
 * @param {{ now?: Date }} [opts]
 */
export function buildTierView(summary, { now = new Date() } = {}) {
  const kind = resolveTierViewKind(summary, now);
  if (!kind) return null;

  // 다음 결정일은 이 뷰를 만드는 시각 기준 — 요약에 굳은 날짜(요약을 만든 때)를 쓰면 열어 둔 탭이 달이 바뀌어도 지난 날짜를 보였다(130차 #2)
  const next = nextConfirmDateLabel(now);
  const dormant = kind === "dormant" || kind === "rookieDormant";
  const rookieLike = ROOKIE_LIKE_KINDS.includes(kind);
  const confirmedTier = summary.confirmedTier || summary.primaryTier;
  // 표시 등급 — 카탈로그 결정: 오늘 기준이 적용 등급과 다르면(up/down) **적용 등급을 크게**, 오늘
  // 기준은 보조 줄("오늘 기준 골드")로. 훅의 primaryTier(=오늘)는 그 외 상태에서만 주인공이다.
  // 「승급 승인」은 이미 실버다 — 루키 화면 틀(진행 점 · 내 등급 루키 화면)은 쓰되 등급명·심볼은 실버(114차 #1).
  //   승인은 곧 실버 확정이다(「승인되면 바로 실버로 시작」) — 머리는 늘 확정된 실버(본문 「이제 실버」와 같게 · 116차 #1)이고, 일별 재계산이 다른 띠를 냈으면
  //   정식 카드(up/down)처럼 보조 줄에 「오늘 기준 ○○」로 보인다(117차 #6). 정식 등급은 다음 확정 때
  const approvedTier = kind === "reviewApproved" ? CREATOR_TIER_NAMES[0] : null;
  const tier = approvedTier || (rookieLike ? ROOKIE_LABEL : kind === "up" || kind === "down" ? confirmedTier : summary.primaryTier);
  const monthText = summary.confirmedMonth ? monthLabel(summary.confirmedMonth) : "";
  const cur = summary.rookieProgress?.current ?? 0;
  const target = summary.rookieProgress?.target ?? ROOKIE_VIDEO_THRESHOLD;
  const remaining = Math.max(0, target - cur);
  const practice = Boolean(summary.rookiePracticeOnly);
  // 루키 진행도 단위. 문장 여러 곳에 `${wl}` 로 끼워 넣으므로 **여기서** 옮겨야 한다
  const wl = practice ? tt("a.tier.rookieWorkVideos", "루키 작품 영상") : tt("a.tier.workVideos", "작품 영상");
  const pctText = !rookieLike && !dormant && summary.percentileOverall !== null && summary.percentileOverall !== undefined
    ? tt("a.tier.topPct", "상위 {{v0}}%", { v0: summary.percentileOverall })
    : null;
  const lastShorts = shortsDateLabel(summary.lastShortsAt, now);
  const lastShortsDays = daysSince(summary.lastShortsAt, now);
  const lastShortsLine = lastShorts
    ? [seg(tt("a.db0e2065", "마지막 쇼츠") + " "), seg(lastShorts, true), seg(lastShortsDays !== null ? " " + tt("a.abd2aa4d", "· {{v0}}일 전", { v0: lastShortsDays }) : "")]
    : plain(tt("a.96c7bc63", "마지막 쇼츠 없음"));
  const gap = summary.gapToNext; // 문자열 "5.3" 또는 null
  const keep = summary.gapToKeep;
  const nextTier = summary.nextTier;
  const dailyTier = summary.dailyTier;
  // 심사 제도가 꺼져 있으면(서버가 알려 줄 때만) 종전 흐름 — 목표를 채우면 다음 등급 결정일에 정식 등급(app_config.rookie_review_enabled=false · 계산식 is_rookie = rnwv < 5).
  // 켜져 있거나 모르면 지금 흐름(레이블리 심사 → 승인 즉시 실버 · 130차 #6)
  const paused = summary.rookieReviewEnabled === false;
  //   날짜 흐름은 아직 채우는 중인 카드만 — 심사 중·반려·승인·루키로 남기·오늘 정식은 이미 심사 쪽 상태라 섞지 않는다(131차 #1)
  //   휴면 루키도 포함 — 꺼짐이면 서버의 루키 판정은 진행도뿐(is_rookie = rnwv < 5)이라 이미 채운 휴면 루키도 다음 계산에서 정식(휴면) 등급이 된다.
  //   그래서 날짜 흐름이 사실이고, 오른쪽 줄·지금 할 일·사다리·평가 축이 이 값 하나로 같은 길을 말한다(132차 #1 · 133차 #1 — 「심사 뒤 실버」는 꺼짐에서 열리지 않는다)
  const pausedFilling = paused && (["stage0", "stage1", "rookie", "rookieDormant"].includes(kind) || (kind === "rookieFilled" && !summary.formalDailyTier));
  // 사다리·평가 축 카드의 날짜 흐름 — 심사 꺼짐(채우는 중) 또는 오늘 이미 정식(다음 결정 때 정식 카드로 바뀜) · 134차 #2
  //   승인(이미 실버 · 다음 결정 때 정식 카드)도 평가 축은 그 날짜부터(136차 #1)
  const datePath = pausedFilling || (kind === "rookieFilled" && Boolean(summary.formalDailyTier)) || kind === "reviewApproved";
  // 루키(승인 전) 카드의 오른쪽 「다음 등급 결정 · 날짜」 — 지금 흐름이면 날짜가 아니라 심사 → 실버(130차 #1). 승인·오늘 정식은 다음 결정 때 정식 카드로 바뀌므로 날짜 그대로
  const rookieWhen = rookieLike && kind !== "reviewApproved" && !(kind === "rookieFilled" && summary.formalDailyTier) && !pausedFilling
    ? { label: tt("a.34d5db6b", "정식 등급"), value: kind === "rookieStay" ? tt("a.tier.rookieStayBadge", "루키로 활동 중")
        // 이미 채운 휴면 루키 — 휴면 중엔 심사가 열리지 않는다(rookie_promotion_review_due) · 지금 할 일 3단계와 같은 말(134차 #1)
        : kind === "rookieDormant" && cur >= target ? tt("a.d9cf212e", "휴면이 풀린 뒤")
        : tt("a.eaaf8344", "심사 뒤 실버") }
    : null;

  const base = {
    kind,
    tier,
    isRookie: rookieLike && !approvedTier,
    // 루키 진행 단위(「작품 영상」 · 루키 작품만 셀 때 「루키 작품 영상」) — 지금 할 일 카드도 같은 단위로(118차 #3)
    unit: wl,
    target,
    practice,   // 루키 작품만 세는가 — 히어로 점 칸 아래 글(121차 #4)
    // 오늘 일별 계산이 이미 정식 등급인 5편 채움 카드인가 — 지금 할 일·홈 선반이 진행 점 수로 짐작하지 않고 이 값을 본다(125차 #1·#5)
    formalToday: kind === "rookieFilled" && Boolean(summary.formalDailyTier),
    // 심사 제도 꺼짐(종전 흐름) — 지금 할 일·사다리·평가 축 카드가 날짜 흐름으로(130차 #6).
    reviewPaused: pausedFilling,
    // 사다리·평가 축 카드가 날짜 흐름(「날짜, 이 다섯 중 하나로」)인가 — 심사 꺼짐(채우는 중) 또는 오늘 이미 정식(다음 결정 때 정식 카드로 바뀜) · 134차 #2
    datePath,
    // 다음 등급 결정일(날짜) — `when.value` 는 상태에 따라 문장이라 날짜가 필요한 카드는 이 값을 쓴다(131차 #4)
    nextDate: next,
    // 사다리 카드 머리 — date(날짜, 다섯 중 하나) · dormant(휴면이 풀리면 심사 → 실버) · review(심사 → 실버) · 135차 #4
    //   approved(지금은 실버 · 날짜부터 다섯 중 하나) · stay(「실버로 가기」를 고르면) — 136차 #1
    ladderMode: kind === "reviewApproved" ? "approved" : datePath ? "date" : kind === "rookieStay" ? "stay" : kind === "rookieDormant" && cur >= target ? "dormant" : "review",
    // 자동 종료 안내가 닫히는 시각 — 서버 감시 창(168시간)은 자정과 상관없이 닫히므로 그때 다시 그린다(135차 #1)
    refreshAt: summary.rookieReview?.status === "lapsed" ? rollingWindowNextBoundary(summary.rookieReview.reviewedAt, 7, now) : null,
    dormant,
    isTop: kind === "top",
    // 2026-09-02 정정(Daniel 확정, 휴면 표시 정책) — "보존된 등급"은 동결된 등급이 있다는
    // 인상을 주는 kicker라 폐기. 휴면은 그냥 "휴면"으로만 부른다 — 등급명은 아래 각 kind별
    // 필드(tier/badges/coach/tooltip/settings/myTier)에서도 전부 가려진다(휴면 케이스 참고).
    kicker: dormant ? ttl(DORMANT_LABEL) : tt("a.246cbd41", "내 등급"),
    badges: [],
    subline: null,
    pctText,
    mid: null,
    chip: null,
    coach: [],
    when: rookieWhen || { label: tt("a.681bc028", "다음 등급 결정"), value: next },
    cta: null,
    ringPercent: 0,
    mobile: { line2: [], badges: [] },
    tooltip: { title: tier, sub: "" },
    settings: { badge: "", sub: [], right: rookieWhen || { label: tt("a.681bc028", "다음 등급 결정"), value: next }, track: "track" },
    // 2026-09-18 — 마스터는 점수로 "남은 것"을 말할 수 없는 상위 N% 특수 등급이라(위 masterPctText
    // 참고), 다음 등급이 마스터일 때는 "마스터까지 남은 것" 대신 "마스터는 상위 N%"로 제목을 바꾼다.
    myTier: { badge: "", right: [], tagline: "", msg: [], cta: null, nextTitle: nextTier ? (nextTier === MASTER_TIER_NAME ? tt("a.tier.masterIsTopRatioTitle", "마스터는 상위 {{v0}}%", { v0: MASTER_TOP_RATIO }) : tt("a.045bf2ea", "{{v0}}까지 남은 것", { v0: nextTier })) : tt("a.ba32c201", "{{v0}} 유지하는 법", { v0: tier }) },
  };

  switch (kind) {
    case "stage0":
      return {
        ...base,
        subline: plain(tt("a.bf9dcaf9", "첫 번째 등급")),
        mid: { kind: "path", label: tt("a.dac66f50", "등급 여정"), value: tt("a.848764da", "채널 등록부터"), steps: ROOKIE_STEPS(false, tt("a.e2da338a", "{{v0}} {{v1}}편", { v0: wl, v1: target }), false) },
        coach: plain(tt("a.34e784b9", "레이블리의 모든 크리에이터는 루키에서 시작해요. 채널을 등록하면 등급 여정이 시작됩니다.")),
        cta: { label: tt("a.ad855e94", "채널 등록하기"), to: "/channel" },
        // 자동 종료 뒤 채널을 다 지운 회원도 왜 내려갔는지 보인다(stage1·루키와 같은 문장 · 리뷰 50차 #1)
        note: lapsedNote(summary, now),
        mobile: { line2: plain(tt("a.453f0f54", "채널을 등록하면 등급 여정이 시작돼요")), badges: [], pctText: tt("a.bf9dcaf9", "첫 번째 등급") },
        tooltip: { title: tt("a.115b49e9", "{{v0}} · 첫 번째 등급", { v0: ROOKIE_LABEL }), sub: tt("a.1cfbf249", "채널을 등록하면 시작돼요") },
        settings: { badge: tt("a.bf9dcaf9", "첫 번째 등급"), sub: plain(tt("a.453f0f54", "채널을 등록하면 등급 여정이 시작돼요")), right: base.settings.right, track: "dots", dots: 0, dotsLabel: tt("a.dac66f50", "등급 여정"), dotsValue: tt("a.8485f813", "채널 등록 → {{v0}} {{v1}}편 → 정식 등급", { v0: wl, v1: target }) },
        myTier: { ...base.myTier, badge: tt("a.115b49e9", "{{v0}} · 첫 번째 등급", { v0: ROOKIE_LABEL }), right: paused ? [seg(tt("a.681bc028", "다음 등급 결정") + " "), seg(next, true)] : [seg(tt("a.39095ece", "{{v0}}편이면", { v0: target }) + " "), seg(tt("a.4822ab8b", "레이블리 심사"), true)], tagline: tt("a.f2033ba2", "레이블리의 모든 크리에이터가 여기서 시작해요"), msg: plain(tt("a.0aef66e8", "채널을 등록하고 {{v0}} {{v1}}편을 올리면 정식 등급이 결정돼요.", { v0: wl, v1: target })), cta: { label: tt("a.ad855e94", "채널 등록하기"), to: "/channel" } },
      };
    // 2026-09-06 밤(대표 결정) — 5편은 다음 달 1일 자동 결정이 아니라 「레이블리 심사 → 승인 즉시 실버」. 루키 작품만 세는 스위치가
    // 켜져 있으면(rookiePracticeOnly) 「루키 작품 영상」으로 부른다 — 어느 작품이 세는지 화면이 말해 줘야 한다.
    case "stage1":
      return {
        ...base,
        subline: plain(tt("a.bf9dcaf9", "첫 번째 등급")),
        mid: { kind: "path", label: tt("a.d4732486", "정식 등급까지"), value: tt("a.e2da338a", "{{v0}} {{v1}}편", { v0: wl, v1: target }), steps: ROOKIE_STEPS(true, `${wl} 0/${target}`, true) },
        coach: [seg(tt("a.03fef40a", "{{v0}}에 사용 신청을 하고 영상을 올려 보세요. {{v1}} {{v2}}편이 모이면", { v0: practice ? tt("a.tier.rookieWorks", "루키 작품") : tt("a.tier.likedWorks", "마음에 드는 작품"), v1: wl, v2: target }) + " "), ...(paused ? [seg(tt("a.101c1c85", "{{v0}}에 정식 등급", { v0: next }), true), seg(tt("a.a0f8e179", "으로 시작해요."))] : [seg(tt("a.008cc028", "레이블리 심사 뒤 바로 실버"), true), seg(tt("a.6b22293b", "로 시작해요."))])],
        cta: { label: practice ? tt("a.bfe370e8", "루키 작품 둘러보기") : tt("a.598edfef", "작품 둘러보기"), to: "/home" },
        mobile: { line2: [seg(`${wl} `), seg(`0/${target}`, true), seg(" " + tt("a.03f5544a", "· 작품 둘러보기 ›"))], badges: [], pctText: tt("a.bf9dcaf9", "첫 번째 등급") },
        tooltip: { title: `${ROOKIE_LABEL} · ${wl} 0/${target}`, sub: tt("a.598edfef", "작품 둘러보기") },
        settings: { badge: tt("a.bf9dcaf9", "첫 번째 등급"), sub: plain(tt("a.b6ed9ecc", "정식 등급까지 {{v0}} 0/{{v1}}", { v0: wl, v1: target })), right: base.settings.right, track: "dots", dots: 0, dotsLabel: tt("a.d4732486", "정식 등급까지"), dotsValue: `${wl} 0 / ${target}` },
        myTier: { ...base.myTier, badge: tt("a.115b49e9", "{{v0}} · 첫 번째 등급", { v0: ROOKIE_LABEL }), right: [seg(tt("a.39095ece", "{{v0}}편이면", { v0: target }) + " "), paused ? seg(tt("a.b1fbcf00", "{{v0}} 정식 등급", { v0: next }), true) : seg(tt("a.4822ab8b", "레이블리 심사"), true)], tagline: tt("a.f2033ba2", "레이블리의 모든 크리에이터가 여기서 시작해요"), msg: plain(practice ? tt("a.48828a79", "루키 작품 하나를 골라 사용 신청부터 해 보세요.") : tt("a.b34fe30a", "작품 하나를 골라 사용 신청부터 해 보세요.")), cta: { label: practice ? tt("a.bfe370e8", "루키 작품 둘러보기") : tt("a.598edfef", "작품 둘러보기"), to: "/home" } },
        // 2026-10-09 — 영상을 전부 지워 0편이 된 자동 종료도 왜 내려갔는지 보인다(루키 카드와 같은 문장 · 리뷰 28차 #5)
        note: lapsedNote(summary, now),
      };
    case "rookie":
    case "rookieFilled": {
      // rookieFilled — 5편을 채웠는데 심사가 열려 있지 않다(110차 #1 · 111차 #1 · 112차 #2·#3):
      //   심사 제도가 꺼져 있다고 서버가 알려 주면 종전 흐름(다음 등급 결정일에 정식 등급 · 130차 #6) · 그 밖(기존 회원 유예 · 일별 계산 전 · 상태 조회 실패)은 「심사가 열리면 이 카드에」.
      //   알림을 약속하지 않는다 — 자동 종료 뒤 다시 열 때의 알림은 대표 승인 전까지 꺼져 있다(rookie_review_reopen_notify)
      //   「루키로 남기」를 골랐으면 서버가 심사를 열지 않는다 — 바꾸는 곳을 말하되, 바꾸면 곧 열린다고 하지 않는다(휴면 등 다른 까닭으로도 안 열린다 · 113차 #4 · 114차 #3)
      const filled = kind === "rookieFilled";
      const filledPaused = filled && paused;   // 채운 뒤 심사 제도 꺼짐(종전 흐름 — 다음 등급 결정일 · 130차 #6)
      // 심사 상태를 아직 못 읽었으면(로딩·실패 — withRookieReview 전이라 rookieReview 가 undefined) 심사가 열린다·멈췄다·승인됐다 어느 것도 말하지 않는다 —
      // 채운 사실만(120차 #1 · 정식 일별 행만 보고 「승인」이라 짐작하면 기존 회원·심사 꺼짐 회원에게 받은 적 없는 승인이 뜬다)
      const unknown = filled && !filledPaused && summary.rookieReview === undefined;
      // 오늘 정식 등급이면 그 사실이 먼저(심사 제도·루키로 남기 문장보다 · 124차 #1) — 판정은 base.formalToday 한 곳(126차 #4)
      const formalToday = base.formalToday ? summary.formalDailyTier : null;
      // 오늘 정식이면 「루키로 남기」 문장·머리 글을 쓰지 않는다(125차 #3 — 「오늘 기준 ○○」과 「루키 작품으로 계속 활동하는 중」이 한 카드에 같이 떴다)
      const stay = filled && !filledPaused && !unknown && !formalToday && Boolean(summary.rookieStay);
      const pausedText = formalToday
        ? unknown
          ? tt("a.0a0c7b01", "오늘 기준 {{v0}} 등급으로 계산됐어요. 승급 심사 상태를 불러오면 이 카드에 자세히 보여 드려요.", { v0: ttl(formalToday) })
          : tt("a.c17facdc", "오늘 기준 {{v0}} 등급으로 계산됐어요.", { v0: ttl(formalToday) })
        : unknown
        ? tt("a.tier.reviewFilledUnknown", "{{v0}} {{v1}}편을 채웠어요.", { v0: wl, v1: target })
        : filledPaused
        ? tt("a.tier.filledMonthly", "{{v0}} {{v1}}편을 채웠어요. {{v2}}에 정식 등급이 정해져요.", { v0: wl, v1: target, v2: next })   // 심사 제도 꺼짐 = 종전 흐름(130차 #6)
        : stay
          ? tt("a.tier.reviewFilledStay", "{{v0}} {{v1}}편을 채웠어요. 「루키로 남기」를 골라 두셔서 지금은 승급 심사가 열리지 않아요. 실버로 가고 싶으면 내 등급에서 선택을 바꿔 주세요 — 심사가 열리면 이 카드에 보여 드려요.", { v0: wl, v1: target })
          : tt("a.tier.reviewFilled", "{{v0}} {{v1}}편을 채웠어요. 승급 심사가 열리면 이 카드에 보여 드려요.", { v0: wl, v1: target });
      const pausedShort = formalToday
        ? tt("a.941df763", "오늘 기준 {{v0}}", { v0: ttl(formalToday) })
        : unknown
        ? tt("a.tier.reviewFilledUnknownShort", "{{v1}}편 채움", { v1: target })
        : filledPaused
        ? tt("a.tier.filledMonthlyShort", "{{v1}}편 채움 · {{v2}} 정식 등급", { v1: target, v2: next })
        : stay
          ? tt("a.tier.reviewFilledStayShort", "{{v1}}편 채움 · 루키로 남기", { v1: target })
          : tt("a.tier.reviewFilledShort", "{{v1}}편 채움 · 심사가 열리면 여기에", { v1: target });
      const view = {
        ...base,
        subline: plain(tt("a.bf9dcaf9", "첫 번째 등급")),
        mid: { kind: "dots", label: tt("a.d4732486", "정식 등급까지"), value: remainText(remaining), n: cur, total: target, left: tt("a.e2da338a", "{{v0}} {{v1}}편", { v0: wl, v1: cur }), right: tt("a.33e4a362", "{{v0}}편", { v0: target }) },
        // 문장 한 키 + **굵게** 표시(2026-09-07 전수 검수) — 세 조각을 이어 붙이면 영어 어순이 깨진다
        // 심사 제도가 꺼져 있으면 종전 흐름(다음 등급 결정일에 정식 등급 · 130차 #6)
        coach: paused
          ? remaining === 1
            ? segsFromMarked(tt("a.84e245b3", "{{v0}} 한 편만 더 올리면 **{{v1}}에 정식 등급**이 정해져요.", { v0: wl, v1: next }))
            : segsFromMarked(tt("a.4a33ed06", "{{v0}} {{v2}}편을 더 올리면 **{{v1}}에 정식 등급**이 정해져요.", { v0: wl, v1: next, v2: remaining }))
          : remaining === 1
          ? segsFromMarked(tt("a.b5b83375", "{{v0}} 한 편만 더 올리면 **레이블리 심사가 시작**돼요. 승인되면 바로 실버로 시작합니다.", { v0: wl }))
          : segsFromMarked(tt("a.f278f1fb", "{{v0}} {{v1}}편을 더 올리면 **레이블리 심사가 시작**돼요. 승인되면 바로 실버로 시작합니다.", { v0: wl, v1: remaining })),
        cta: { label: practice ? tt("a.bfe370e8", "루키 작품 둘러보기") : tt("a.598edfef", "작품 둘러보기"), to: "/home" },
        ringPercent: Math.min(100, Math.round((cur / target) * 100)),
        mobile: { line2: [remaining === 1 ? seg(tt("a.0bddbf05", "한 편만 더"), true) : seg(tt("a.f844b3bd", "{{v0}}편 더", { v0: remaining }), true), seg(paused ? tt("a.7807957f", "하면 {{v0}}에 정식 등급", { v0: next }) : tt("a.d04e9e7a", "하면 레이블리 심사 · 승인 즉시 실버"))], badges: [], pctText: `${cur}/${target}` },
        tooltip: { title: `${ROOKIE_LABEL} · ${wl} ${cur}/${target}`, sub: paused ? tt("a.c015c22b", "{{v0}} · {{v1}} 정식 등급", { v0: remainText(remaining), v1: next }) : tt("a.0fd81745", "{{v0}} · 심사 뒤 실버", { v0: remainText(remaining) }) },
        settings: { badge: tt("a.bf9dcaf9", "첫 번째 등급"), sub: plain(tt("a.595cf67d", "정식 등급까지 {{v0}} {{v1}}/{{v2}} · {{v3}}", { v0: wl, v1: cur, v2: target, v3: remainText(remaining) })), right: base.settings.right, track: "dots", dots: cur, dotsLabel: tt("a.d4732486", "정식 등급까지"), dotsValue: `${wl} ${cur} / ${target}` },
        myTier: { ...base.myTier, badge: tt("a.115b49e9", "{{v0}} · 첫 번째 등급", { v0: ROOKIE_LABEL }), right: [seg(tt("a.39095ece", "{{v0}}편이면", { v0: target }) + " "), paused ? seg(tt("a.b1fbcf00", "{{v0}} 정식 등급", { v0: next }), true) : seg(tt("a.4822ab8b", "레이블리 심사"), true)], tagline: tt("a.f2033ba2", "레이블리의 모든 크리에이터가 여기서 시작해요"), msg: plain(paused
          ? remaining === 1 ? tt("a.39fd47ad", "한 편만 더 올리면 {{v0}}에 정식 등급이 정해져요.", { v0: next }) : tt("a.423853f0", "{{v0}}편을 더 올리면 {{v1}}에 정식 등급이 정해져요.", { v0: remaining, v1: next })
          : remaining === 1 ? tt("a.cd0f72d6", "한 편만 더 올리면 레이블리 심사 뒤 바로 실버로 시작해요.") : tt("a.989eee93", "{{v0}}편을 더 올리면 레이블리 심사 뒤 바로 실버로 시작해요.", { v0: remaining })), cta: { label: practice ? tt("a.bfe370e8", "루키 작품 둘러보기") : tt("a.598edfef", "작품 둘러보기"), to: "/home" } },
        // 2026-10-09 — 자동 종료(lapsed) 뒤 7일 안이면 왜 진행도가 내려갔는지 심사 행의 문장을 「심사 안내」 카드로 보인다(알림은 대표 승인 뒤 — 카드가 없으면 조용히 떨어진 것만 보인다 · 리뷰 8차 #6)
        note: lapsedNote(summary, now),
      };
      if (!filled) return view;
      // 자동 종료 안내(note)는 종료 까닭만 — 「심사 꺼짐(종전 흐름)」이 두 카드에 겹치지 않게(111차 #2), 「다시 채우면 자동으로 다시 열려요」를
      // 약속하지 않게(심사 반려 뒤 같은 영상만 되살리면 서버가 다시 열지 않는다 · 112차 #5). 지금 상태는 위 문장이 말한다
      //   오늘 정식이면 자동 종료 안내를 붙이지 않는다 — 「오늘 기준 ○○」 옆에서 할 필요 없는 일을 시키는 것처럼 읽힌다(128차 #6)
      const note = formalToday
        ? null
        : view.note
          ? tt("a.tier.lapsedOnly", "올린 작품 영상 중 삭제되거나 비공개된 영상이 있어 승급 심사가 자동으로 종료됐어요.")
          : view.note;
      // 오늘 정식이면 편수(3/5) 점·「N편이 되면 정식 등급」을 그리지 않는다 — 「이미 정식」 옆에 「2편 더 필요」로 읽히지 않게(126차 #2).
      // 히어로는 단계(채널 → 작품 영상 → 정식 등급 지금), 모바일 숫자 자리는 지금 상태, 설정 화면은 진행 칸 없이 문장만
      const formalMid = formalToday
        ? { kind: "path", label: tt("a.dac66f50", "등급 여정"), value: pausedShort,
            steps: [{ text: tt("a.c164f7de", "채널 등록"), state: "done" }, { text: wl, state: "done" }, { text: tt("a.34d5db6b", "정식 등급"), state: "now" }] }
        : null;
      return {
        ...view,
        note,
        mid: formalMid || { ...view.mid, value: pausedShort },
        ringPercent: formalMid ? 100 : view.ringPercent,
        coach: [seg(pausedText)],
        // 편수 자리는 비우고(지금 상태는 바로 아래 줄 · 같은 문장을 두 번 쓰지 않게 · 127차 #3)
        mobile: { ...view.mobile, line2: [seg(pausedShort, true)], ...(formalMid ? { pctText: "" } : null) },
        tooltip: { ...view.tooltip, sub: pausedShort, ...(formalMid ? { title: ROOKIE_LABEL } : null) },
        settings: { ...view.settings, sub: plain(pausedShort), ...(formalMid ? { track: "none", dotsValue: pausedShort } : null) },
        // 오른쪽 줄(「5편이면 레이블리 심사」)도 지금 상태로 — 「심사 꺼짐(종전 흐름)」·「루키로 남기」 문장 옆에서 심사를 약속하지 않게(117차 #3)
        myTier: { ...view.myTier, msg: plain(pausedText), right: [seg(pausedShort, true)], tagline: stay ? tt("a.tier.rookieStayTagline", "루키 작품으로 계속 활동하는 중이에요") : view.myTier.tagline },
      };
    }
    // 2026-09-06 — 승급 심사 중(대표 결정). 5/5 도트 그대로, 값만 「승급 심사 중」. 날짜 대신 "결과는 알림으로".
    //   진행 점 칸의 단위는 루키 카드와 같은 `wl`(루키 작품만 셀 때 「루키 작품 영상」 · 116차 #4) — 심사·승인·반려·휴면 루키 카드 공통
    case "review": {
      const coach = segsFromMarked(tt("a.df1e52b7", "{{v0}} {{v1}}편을 채웠어요. 레이블리가 영상을 확인한 뒤 **실버 등급을 바로 확정**해 드려요. 결과는 알림으로 알려 드릴게요.", { v0: wl, v1: target }));
      return {
        ...base,
        subline: plain(tt("a.bf9dcaf9", "첫 번째 등급")),
        mid: { kind: "dots", label: tt("a.d4732486", "정식 등급까지"), value: tt("a.14f25462", "승급 심사 중"), n: target, total: target, left: tt("a.e2da338a", "{{v0}} {{v1}}편", { v0: wl, v1: target }), right: tt("a.33e4a362", "{{v0}}편", { v0: target }) },
        coach,
        cta: { label: tt("a.a65b6b76", "내 등급 보기"), to: "/my-tier" },
        ringPercent: 100,
        mobile: { line2: [seg(tt("a.14f25462", "승급 심사 중"), true), seg(" " + tt("a.828ca316", "· 결과는 알림으로"))], badges: [], pctText: `${target}/${target}` },
        tooltip: { title: tt("a.99944b11", "{{v0}} · 승급 심사 중", { v0: ROOKIE_LABEL }), sub: tt("a.tier.wlCountSlash", "{{v0}} {{v1}}/{{v2}}", { v0: wl, v1: target, v2: target }) },
        settings: { badge: tt("a.14f25462", "승급 심사 중"), sub: plain(tt("a.tier.wlReviewSettingsSub", "{{v0}} {{v1}}/{{v2}} · 결과는 알림으로 알려 드려요", { v0: wl, v1: target, v2: target })), right: base.settings.right, track: "dots", dots: target, dotsLabel: tt("a.d4732486", "정식 등급까지"), dotsValue: tt("a.tier.wlDotsReview", "{{v0}} {{v1}} / {{v2}} · 승급 심사 중", { v0: wl, v1: target, v2: target }) },
        myTier: { ...base.myTier, badge: tt("a.99944b11", "{{v0}} · 승급 심사 중", { v0: ROOKIE_LABEL }), right: [seg(tt("a.511fe1ff", "심사 결과") + " "), seg(tt("a.6f497c8a", "알림으로"), true)], tagline: tt("a.74a2e4b4", "레이블리가 영상을 확인하고 있어요"), msg: coach, cta: null },
      };
    }
    // 승급 심사 승인 — 실버로 확정됐는데 아직 정식 월 행이 없는 회원(월 확정 루키 · 일별 진행도만 있는 pre-rookie · 112차 #1 · 113차 #1). 등급 카드는 다음 확정 때 정식 등급으로 바뀐다
    case "reviewApproved": {
      const dailyApproved = summary.primaryTier || summary.formalDailyTier || null;   // 월 행 없는 회원은 formalDailyTier 만 있다(120차 #4)
      const coach = segsFromMarked(tt("a.tier.approvedCoach", "승급 심사가 승인됐어요. **이제 실버**예요 — 등급 카드는 {{v0}} 확정 때 정식 등급으로 바뀌어요.", { v0: next }));
      const short = tt("a.tier.approvedShort", "승급 승인 · 실버");
      return {
        ...base,
        // 머리는 승인으로 확정된 실버(본문 「이제 실버」와 같게 · 116차 #1). 일별 재계산이 다른 띠를 냈으면 정식 카드처럼 보조 줄에 「오늘 기준」으로(117차 #6)
        subline: dailyApproved && dailyApproved !== tier
          ? [seg(tt("a.680e4e82", "오늘 기준") + " "), seg(dailyApproved, true)]
          : plain(tt("a.tier.approvedSubline", "첫 정식 등급")),
        badges: [{ kind: "gold", text: short }],
        mid: { kind: "dots", label: tt("a.tier.approvedMidLabel", "승급 심사"), value: short, n: target, total: target, left: tt("a.e2da338a", "{{v0}} {{v1}}편", { v0: wl, v1: target }), right: tt("a.33e4a362", "{{v0}}편", { v0: target }) },
        coach,
        cta: { label: tt("a.a65b6b76", "내 등급 보기"), to: "/my-tier" },
        ringPercent: 100,
        mobile: { line2: [seg(short, true)], badges: [], pctText: `${target}/${target}` },
        tooltip: { title: tt("a.tier.approvedTitle", "{{v0}} · 승급 승인", { v0: ttl(tier) }), sub: short },
        settings: { badge: short, sub: plain(short), right: base.settings.right, track: "dots", dots: target, dotsLabel: tt("a.tier.approvedMidLabel", "승급 심사"), dotsValue: short },
        myTier: { ...base.myTier, badge: tt("a.tier.approvedTitle", "{{v0}} · 승급 승인", { v0: ttl(tier) }), right: [seg(short, true)], tagline: "", msg: coach, cta: null },
      };
    }
    // 2026-10-02 — 「루키로 남기」(대표 결정): 5편을 채웠지만 본인이 루키 유지를 골랐다. 심사는 열리지 않고 배정 루키 작품을 계속 쓴다.
    //   실버로 마음이 바뀌면 /my-tier 의 선택 카드에서 「실버로 가기」(set_rookie_stay(false) + 심사 신청). 감점·멤버십 구독료 숫자 없음.
    case "rookieStay": {
      const coach = segsFromMarked(tt("a.tier.rookieStayCoach", "{{v0}} {{v1}}편을 채웠고 **루키로 남기**를 골랐어요. 배정된 루키 작품을 계속 쓸 수 있고, 실버로 가고 싶으면 내 등급에서 언제든 바꿀 수 있어요.", { v0: wl, v1: target }));
      return {
        ...base,
        subline: plain(tt("a.bf9dcaf9", "첫 번째 등급")),
        badges: [{ kind: "light", text: tt("a.tier.rookieStayBadge", "루키로 활동 중") }],
        mid: { kind: "dots", label: tt("a.d4732486", "정식 등급까지"), value: tt("a.tier.rookieStayBadge", "루키로 활동 중"), n: target, total: target, left: tt("a.e2da338a", "{{v0}} {{v1}}편", { v0: wl, v1: target }), right: tt("a.33e4a362", "{{v0}}편", { v0: target }) },
        coach,
        cta: { label: tt("a.a65b6b76", "내 등급 보기"), to: "/my-tier" },
        ringPercent: 100,
        mobile: { line2: [seg(tt("a.tier.rookieStayBadge", "루키로 활동 중"), true), seg(" " + tt("a.tier.rookieStayMobile", "· 실버는 내 등급에서"))], badges: [], pctText: `${target}/${target}` },
        tooltip: { title: tt("a.tier.rookieStayTitle", "{{v0}} · 루키로 활동 중", { v0: ROOKIE_LABEL }), sub: tt("a.tier.rookieStaySub", "{{v0}} {{v1}}/{{v2}} · 실버는 내 등급에서 바꿀 수 있어요", { v0: wl, v1: target, v2: target }) },
        settings: { badge: tt("a.tier.rookieStayBadge", "루키로 활동 중"), sub: plain(tt("a.tier.rookieStaySub", "{{v0}} {{v1}}/{{v2}} · 실버는 내 등급에서 바꿀 수 있어요", { v0: wl, v1: target, v2: target })), right: base.settings.right, track: "dots", dots: target, dotsLabel: tt("a.d4732486", "정식 등급까지"), dotsValue: `${wl} ${target} / ${target}` },
        myTier: { ...base.myTier, badge: tt("a.tier.rookieStayTitle", "{{v0}} · 루키로 활동 중", { v0: ROOKIE_LABEL }), right: [seg(tt("a.tier.rookieStayRight", "실버로 가기는") + " "), seg(tt("a.tier.rookieStayRightStrong", "아래 카드에서"), true)], tagline: tt("a.tier.rookieStayTagline", "루키 작품으로 계속 활동하는 중이에요"), msg: coach, cta: null },
      };
    }
    // 2026-09-06 — 심사 반려 → 보완 뒤 다시 신청. `note` 는 어드민(심사자)이 쓴 안내 문장을 **그대로** 인용한다 —
    // 생성 문장이 아니라 심사자의 텍스트. cta.action 은 /my-tier 만 안다(다른 화면은 `to` 로 이동만).
    case "reviewRejected": {
      const coach = [seg(tt("a.480a5d20", "승급 심사에서 보완 요청이 있었어요.") + " "), seg(tt("a.b48a6865", "안내를 확인하고 보완한 뒤 다시 신청"), true), seg(tt("a.471f6b1f", "할 수 있어요."))];
      const cta = { label: tt("a.95dce757", "다시 신청"), to: "/my-tier", action: "rookieReviewRequest" };
      return {
        ...base,
        badges: [{ kind: "light", text: tt("a.45e36389", "보완 필요") }],
        subline: plain(tt("a.bf9dcaf9", "첫 번째 등급")),
        mid: { kind: "dots", label: tt("a.d4732486", "정식 등급까지"), value: tt("a.45e36389", "보완 필요"), n: target, total: target, left: tt("a.e2da338a", "{{v0}} {{v1}}편", { v0: wl, v1: target }), right: tt("a.33e4a362", "{{v0}}편", { v0: target }) },
        coach,
        note: summary.rookieReview?.creatorMessage || null,
        cta,
        ringPercent: 100,
        mobile: { line2: [seg(tt("a.45e36389", "보완 필요"), true), seg(" " + tt("a.99179c9c", "· 안내 확인 뒤 다시 신청"))], badges: [{ kind: "dark", text: tt("a.45e36389", "보완 필요") }], pctText: `${target}/${target}` },
        tooltip: { title: tt("a.178eb3a6", "{{v0}} · 보완 필요", { v0: ROOKIE_LABEL }), sub: tt("a.fc03109e", "안내를 확인하고 다시 신청할 수 있어요") },
        settings: { badge: tt("a.45e36389", "보완 필요"), sub: plain(tt("a.7ca994ff", "승급 심사에서 보완 요청이 있었어요 · 안내를 확인하고 다시 신청")), right: base.settings.right, track: "dots", dots: target, dotsLabel: tt("a.d4732486", "정식 등급까지"), dotsValue: tt("a.tier.wlDotsRejected", "{{v0}} {{v1}} / {{v2}} · 보완 필요", { v0: wl, v1: target, v2: target }) },
        myTier: { ...base.myTier, badge: tt("a.178eb3a6", "{{v0}} · 보완 필요", { v0: ROOKIE_LABEL }), right: [seg(tt("a.fd137280", "보완한 뒤") + " "), seg(tt("a.95dce757", "다시 신청"), true)], tagline: tt("a.dee43568", "안내를 확인하고 보완해 주세요"), msg: coach, cta },
      };
    }
    case "rookieDormant":
      return {
        ...base,
        badges: [{ kind: "light", text: DORMANT_LABEL }],
        subline: lastShortsLine,
        mid: cur > 0
          ? { kind: "dots", label: tt("a.d4732486", "정식 등급까지"), value: remaining > 0 ? tt("a.3e04bccd", "{{v0}} · 이어서 진행", { v0: remainText(remaining) }) : tt("a.f8389c60", "{{v0}}편 채움 · 진행도 보존 중", { v0: target }), n: cur, total: target, left: tt("a.e2da338a", "{{v0}} {{v1}}편", { v0: wl, v1: cur }), right: tt("a.33e4a362", "{{v0}}편", { v0: target }) }
          : { kind: "path", label: tt("a.d4732486", "정식 등급까지"), value: tt("a.e2da338a", "{{v0}} {{v1}}편", { v0: wl, v1: target }), steps: ROOKIE_STEPS(true, `${wl} 0/${target}`, true) },
        // 지금 흐름(5편 → 레이블리 심사 → 승인 즉시 실버)대로 · 단위는 wl · 이미 목표를 채웠으면 「○편이 되면」을 말하지 않는다(128차 #1 — 옛 「다음 달 1일에 정식 등급」 문장)
        coach: remaining > 0 && paused
          ? segsFromMarked(tt("a.64da7c18", "다시 시작하면 진행도가 그대로 이어져요. {{v0}} 1편이면 다음 날 휴면이 풀리고, {{v1}}편이 되면 **{{v2}}에 정식 등급**이 정해져요.", { v0: wl, v1: target, v2: next }))
          : remaining > 0
          ? segsFromMarked(tt("a.8cc6ad5f", "다시 시작하면 진행도가 그대로 이어져요. {{v0}} 1편이면 다음 날 휴면이 풀리고, {{v1}}편이 되면 **레이블리 심사 뒤 바로 실버**로 시작해요.", { v0: wl, v1: target }))
          // 심사 꺼짐이면 이미 채운 휴면 루키도 다음 결정일에 정식(휴면) 등급 — 지금 할 일 3단계와 같은 날짜(134차 #3)
          : paused
          // 날짜는 조건 없이 먼저 · 휴면은 등급을 「보는」 조건 — 지금 할 일 3단계와 같은 말(135차 #3)
          ? plain(tt("a.86609de3", "{{v1}}에 정식 등급이 정해져요. {{v0}} 1편이면 다음 날 휴면이 풀리고 등급을 볼 수 있어요.", { v0: wl, v1: next }))
          : plain(tt("a.bb324866", "다시 시작하면 진행도가 그대로 이어져요. {{v0}} 1편이면 다음 날 휴면이 풀려요.", { v0: wl })),
        cta: { label: tt("a.598edfef", "작품 둘러보기"), to: "/home" },
        ringPercent: 0,
        // 2026-10-09 — 최근 영상을 지워 휴면까지 된 자동 종료도 왜 심사가 자동 종료됐는지 보인다(루키 카드와 같은 문장 · 리뷰 36차 #3)
        note: lapsedNote(summary, now),
        mobile: { line2: [seg(tt("a.c8ae6248", "{{v0}}/{{v1}} 보존 중 ·", { v0: cur, v1: target }) + " "), seg(tt("a.2a7ba48e", "{{v0}} 1편이면 다시 시작", { v0: wl }), true)], badges: [{ kind: "dark", text: DORMANT_LABEL }] },
        tooltip: { title: `${ROOKIE_LABEL} · ${DORMANT_LABEL}`, sub: tt("a.78dffa79", "{{v0}}{{v1}} 1편이면 다시 시작", { v0: lastShorts ? tt("a.tier.lastShortsPrefix", "마지막 쇼츠 {{v0}} · ", { v0: lastShorts }) : "", v1: wl }) },
        settings: { badge: tt("a.f766039f", "첫 번째 등급 · {{v0}}", { v0: DORMANT_LABEL }), sub: plain(tt("a.tier.wlDormantSettingsSub", "정식 등급까지 {{v0}} {{v1}}/{{v2}} · 진행도 보존 중", { v0: wl, v1: cur, v2: target })), right: { label: tt("a.db0e2065", "마지막 쇼츠"), value: lastShorts || tt("a.a245e6fe", "없음") }, track: "dots", dots: cur, dotsLabel: tt("a.d4732486", "정식 등급까지"), dotsValue: tt("a.tier.wlDotsKept", "{{v0}} {{v1}} / {{v2}} · 보존 중", { v0: wl, v1: cur, v2: target }) },
        myTier: { ...base.myTier, badge: `${ROOKIE_LABEL} · ${DORMANT_LABEL}`, right: lastShortsLine, tagline: tt("a.8cf94580", "진행도는 그대로 남아 있어요"), msg: [seg(tt("a.e4266517", "복귀 조건은 하나 —") + " "), seg(tt("a.46f2dc7d", "{{v0}} 1편.", { v0: wl }), true), seg(" " + tt("a.a85df22b", "다음 날부터 진행도가 다시 움직여요."))], cta: { label: tt("a.598edfef", "작품 둘러보기"), to: "/home" } },
      };
    case "dormant": {
      // 2026-09-02 정정(Daniel 확정, 휴면 표시 정책 — "N명 중 M위" 표기 철회 건과 같은 날 별도
      // 확정): 정식 출신 휴면은 여기 있던 `confirmedTier`(동결된 실제 등급)를 어떤 필드에도
      // 넣지 않는다. 예전 문구("돌아오면 {등급} 그대로 배정 후보에 복귀해요", "{등급} 보존
      // 중")는 두 가지 문제가 있었다 — ①실제 등급명이 그대로 노출됨 ②산정 로직은 복귀 시
      // 유예 없이 실위치로 재산정(감쇠 적용, 강등 가능)인데 문구는 "그대로 시작"을 약속해
      // 화면과 동작이 어긋났다. tier 자체도 `DORMANT_LABEL`로 바꿔서(다른 kind처럼 실제
      // 등급명이 아니라) 이 값을 그대로 쓰는 심볼·헤드라인 소비처가 등급을 그릴 수 없게
      // 만들었다(구조적 방지 — 소비처의 `dormant` 분기를 깜빡해도 실제 등급이 새지 않는다).
      //
      // 같은 날 정정(사용자 확정) — 첫 구현이 등급 은닉 작업 중 아래 "복귀 조건"
      // 안내("작품 영상 1편 또는 검수 1건 → 다음 날부터 배정 후보 복귀")를 실수로 함께
      // 지우고 `DORMANT_STATUS_HINT` 하나로 뭉쳤다. 이 안내는 등급 노출과 무관한 사실 정보라
      // (실제 판정 로직과 정확히 일치 — 90일 무활동 진입, 하나만 생기면 다음 일별 크론에서
      // 해제) 등급 은닉과 별개로 보존해야 한다. 그래서 아래 각 필드는 **두 문장 구조**를 쓴다:
      // (1) 등급 보존 약속 대신 "최근 활동 기준 재산정" 원칙(DORMANT_STATUS_HINT) — (2) 그
      // 뒤에 이어붙이는 구체적 복귀 조건 문장(원본 main 카피의 "복귀 조건은 하나 — 작품
      // 영상 1편 또는 검수 1건. 다음 날부터 배정 후보에 다시 들어가요"를 등급명만 빼고 그대로
      // 보존). 등급명·심볼·백분위 미표시 원칙은 그대로 유지.
      const months = dormantMonthsLabel(lastShortsDays);
      const returnCondition = tt("a.tier.returnCondition", "작품 영상 1편 또는 검수 1건");
      return {
        ...base,
        tier: DORMANT_LABEL,
        badges: [{ kind: "light", text: months || DORMANT_LABEL }],
        subline: lastShortsLine,
        mid: { kind: "plain", label: tt("a.5de86b25", "복귀 조건"), value: tt("a.9944348e", "작품 영상 1편"), text: [seg(tt("a.fcd08a27", "돌아오면 최근 활동 기준으로 등급이 다시 계산돼요.") + " "), seg(tt("a.c71eccf4", "영상 한 편이면 다음 날부터 바로."), true)] },
        coach: [seg(tt("a.e5624124", "돌아오면 최근 활동 기준으로 등급이 다시 계산돼요. 복귀 조건은") + " "), seg(returnCondition, true), seg(" " + tt("a.195c3f26", "— 다음 날부터 배정 후보에 다시 들어가요."))],
        cta: { label: tt("a.598edfef", "작품 둘러보기"), to: "/home" },
        ringPercent: 0,
        mobile: { line2: [seg(tt("a.200218a1", "휴면 중 ·") + " "), seg(tt("a.b6bff36b", "영상 1편이면 복귀"), true)], badges: [{ kind: "dark", text: DORMANT_LABEL }] },
        tooltip: { title: DORMANT_LABEL, sub: tt("a.fd658ba6", "{{v0}}영상 1편이면 복귀", { v0: lastShorts ? tt("a.tier.lastShortsPrefix", "마지막 쇼츠 {{v0}} · ", { v0: lastShorts }) : "" }) },
        settings: { badge: months || DORMANT_LABEL, sub: plain(tt("a.55c71069", "재개하면 최근 활동 기준으로 다시 계산돼요 · 영상 1편이면 다음 날부터")), right: { label: tt("a.db0e2065", "마지막 쇼츠"), value: lastShorts || tt("a.a245e6fe", "없음") }, track: "none" },
        myTier: { ...base.myTier, badge: months || DORMANT_LABEL, right: lastShortsLine, tagline: tt("a.62318e41", "돌아오면 최근 활동 기준으로 등급이 다시 계산돼요"), msg: [seg(tt("a.e4266517", "복귀 조건은 하나 —") + " "), seg(`${returnCondition}.`, true), seg(" " + tt("a.9a222c76", "다음 날부터 배정 후보에 다시 들어가요."))], cta: { label: tt("a.598edfef", "작품 둘러보기"), to: "/home" } },
      };
    }
    case "debut":
      return {
        ...base,
        badges: [{ kind: "gold", text: tt("a.5c40baa5", "첫 정식 등급") }],
        subline: [seg(tt("a.deabd137", "루키에서 데뷔했어요"))],
        sublineSymbol: ROOKIE_LABEL,
        mid: gauge(summary, nextTier, gap),
        // 2026-10-26 만족도 제외 — 점수에 들어가는 세 축만 말한다(tierSatisfaction.js)
        coach: TIER_SATISFACTION_IN_SCORE
          ? [seg(tt("a.6f8ec73b", "이제부터") + " "), seg(tt("a.adff8d98", "만족도·성과·꾸준함·참여"), true), seg(" " + tt("a.07854036", "네 가지로 평가돼요. 실버는 시작점이에요 — 첫 달엔 꾸준함(매주 업로드)이 가장 빨리 오릅니다."))]
          // 「자료 방식」(2026-11-01~) — 이번 달 목표 달성률
          : isLinearNow(now)
          ? [seg(tt("a.6f8ec73b", "이제부터") + " "), seg(tt("tier.lin2.coachAxes", "성과·참여·꾸준함"), true), seg(" " + tt("tier.lin2.coachRest", "세 가지를 이번 달 목표에 얼마나 채웠는지로 평가돼요. 실버는 시작점이에요 — 7일마다 고르게 올리면 꾸준함과 참여가 함께 올라요."))]
          : [seg(tt("a.6f8ec73b", "이제부터") + " "), seg(tt("tier.satOff.coachAxes", "성과·꾸준함·참여"), true), seg(" " + tt("tier.satOff.coachRest", "세 가지로 평가돼요. 실버는 시작점이에요 — 첫 달엔 꾸준함(매주 업로드)이 가장 빨리 오릅니다."))],
        ringPercent: summary.progressPercent ?? 0,
        mobile: { line2: gapLine(nextTier, gap, next, summary), badges: [{ kind: "gold", text: tt("a.5c40baa5", "첫 정식 등급") }] },
        tooltip: { title: tt("a.03377f40", "{{v0}} · 첫 정식 등급", { v0: ttl(tier) }), sub: gapText(nextTier, gap, summary) },
        settings: { badge: tt("a.d6497cb9", "{{v0}} 등급 · 첫 정식 등급", { v0: ttl(monthText) }), sub: plain(gapText(nextTier, gap, summary)), right: base.settings.right, track: "track" },
        myTier: { ...base.myTier, badge: tt("a.d6497cb9", "{{v0}} 등급 · 첫 정식 등급", { v0: ttl(monthText) }), right: todayLine(summary), tagline: "", msg: plain(tt("a.91756970", "{{v0}} — 첫 달엔 꾸준함(매주 업로드)이 가장 빨리 올라요", { v0: gapText(nextTier, gap, summary) })), pc2: "루키에서 데뷔 — 이제 네 가지 축으로 평가돼요", histBadge: { kind: "gold", text: tt("a.feb3f012", "데뷔") } },
      };
    case "promoted":
      return {
        ...base,
        badges: [{ kind: "gold", text: tt("a.81af544e", "승급") }],
        subline: [seg(tt("a.7bf91e2a", "{{v0}}에서 올라왔어요", { v0: ttl(summary.promotedFrom) }))],
        sublineSymbol: summary.promotedFrom,
        mid: gauge(summary, nextTier, gap),
        coach: plain(tt("a.1c53aa94", "지난달보다 한 단계 올랐어요. 다음 등급은 {{v0}}에 결정돼요.", { v0: ttl(next) })),
        ringPercent: summary.progressPercent ?? 0,
        mobile: { line2: gapLine(nextTier, gap, next, summary), badges: [{ kind: "gold", text: tt("a.81af544e", "승급") }] },
        tooltip: { title: tt("a.142572f8", "{{v0}} · 승급", { v0: ttl(tier) }), sub: gapText(nextTier, gap, summary) },
        settings: { badge: tt("a.16ed996e", "{{v0}} 등급 · 승급", { v0: ttl(monthText) }), sub: [seg(tt("a.d3480e5b", "{{v0}}에서 올라왔어요 · {{v1}}", { v0: ttl(summary.promotedFrom), v1: gapText(nextTier, gap, summary) }))], subSymbol: summary.promotedFrom, right: base.settings.right, track: "track" },
        myTier: { ...base.myTier, badge: tt("a.16ed996e", "{{v0}} 등급 · 승급", { v0: ttl(monthText) }), right: todayLine(summary), msg: plain(tt("a.c066d532", "{{v0}} — 지난달보다 한 단계 올랐어요", { v0: gapText(nextTier, gap, summary) })), histBadge: { kind: "gold", text: tt("a.81af544e", "승급") } },
      };
    case "demoted": {
      const from = summary.demotedFrom;
      return {
        ...base,
        badges: [{ kind: "dark", text: tt("a.8628c97e", "한 단계 내려옴") }],
        subline: [seg(tt("a.f6dbdee4", "지난달 {{v0}}", { v0: ttl(from) }))],
        sublineSymbol: from,
        mid: { kind: "gauge", label: tt("a.99a5e5d2", "{{v0}}로 돌아가기", { v0: ttl(from) }), value: gap ? tt("a.tier.pts", "+{{v0}}점", { v0: gap }) : "", pct: summary.progressPercent ?? 0, left: tier, right: from },
        coach: [seg(tt("a.07f6d8f5", "{{v0}} 컷에", { v0: ttl(from) }) + " "), seg(gap ? tt("a.8f432065", "{{v0}}점", { v0: gap }) : tt("a.41cc1070", "조금"), true), seg(" " + tt("a.005cfc09", "모자랐어요. 가장 낮은 축부터 되찾으면 {{v0}}에 돌아갈 수 있어요.", { v0: ttl(next) }))],
        ringPercent: summary.progressPercent ?? 0,
        mobile: { line2: [seg(tt("a.7bda3a9d", "{{v0}}로", { v0: ttl(from) }) + " "), seg(gap ? tt("a.c9d49ad6", "+{{v0}}점", { v0: gap }) : "", true), seg(" " + tt("a.b937a250", "· {{v0}} 결정", { v0: ttl(next) }))], badges: [{ kind: "dark", text: tt("a.8628c97e", "한 단계 내려옴") }] },
        tooltip: { title: tt("a.514e41ed", "{{v0}} · 지난달 {{v1}}", { v0: ttl(tier), v1: ttl(from) }), sub: gap ? tt("a.518acda4", "돌아가려면 +{{v0}}점", { v0: gap }) : tt("a.660aa925", "돌아갈 수 있어요") },
        settings: { badge: tt("a.f9942efe", "{{v0}} 등급 · 한 단계 내려옴", { v0: ttl(monthText) }), sub: [seg(tt("a.a8524418", "지난달 {{v0}} · {{v1}}", { v0: ttl(from), v1: gap ? tt("a.tier.backPts", "돌아가려면 +{{v0}}점", { v0: gap }) : "" }))], subSymbol: from, right: base.settings.right, track: "track" },
        myTier: { ...base.myTier, badge: tt("a.f9942efe", "{{v0}} 등급 · 한 단계 내려옴", { v0: ttl(monthText) }), right: todayLine(summary), msg: plain(tt("a.7e904832", "{{v0}} 컷에 {{v1}} 모자랐어요 — 가장 낮은 축부터 되찾으면 돌아갈 수 있어요", { v0: ttl(from), v1: gap ? `${gap}점` : "조금" })), nextTitle: tt("a.99a5e5d2", "{{v0}}로 돌아가기", { v0: ttl(from) }), histBadge: { kind: "dark", text: "↓" } },
      };
    }
    case "up":
      return {
        ...base,
        subline: [seg(tt("a.680e4e82", "오늘 기준") + " "), seg(dailyTier, true)],
        mid: { kind: "gauge", label: tt("a.94cb2ff4", "{{v0}}까지", { v0: ttl(dailyTier) }), value: keep ? tt("a.tier.clearedHeadroom", "넘었어요 · 여유 +{{v0}}점", { v0: keep }) : tt("a.tier.cleared", "넘었어요"), pct: 100, marker: 100, left: tier, right: dailyTier },
        chip: { tone: "light", text: tt("a.6478b238", "이대로면 {{v0}} {{v1}}", { v0: ttl(next), v1: ttl(dailyTier) }) },
        coach: [seg(tt("a.fe1ec885", "남은 기간 동안 이 추이를 지키면 돼요. 컷은 전체 분포로 정해지니") + " "), seg(keep ? tt("a.d86e9793", "여유 {{v0}}점을 더 벌어두면", { v0: keep }) : tt("a.14cf513f", "여유를 더 벌어두면"), true), seg(" " + tt("a.7b15a6b4", "안전해요."))],
        ringPercent: 100,
        mobile: { line2: [seg(tt("a.3cc7defe", "이대로면 {{v0}}", { v0: ttl(dailyTier) }), true), seg(" " + tt("a.b937a250", "· {{v0}} 결정", { v0: ttl(next) }))], badges: [] },
        tooltip: { title: tt("a.d1690f4a", "{{v0}} · 오늘 기준 {{v1}}", { v0: ttl(tier), v1: ttl(dailyTier) }), sub: tt("a.6478b238", "이대로면 {{v0}} {{v1}}", { v0: ttl(next), v1: ttl(dailyTier) }) },
        settings: { badge: tt("a.6cd793b6", "{{v0}} 등급", { v0: ttl(monthText) }), sub: [seg(keep ? tt("a.6d79da1c", "여유 +{{v0}}점", { v0: keep }) : "")], chip: { tone: "light", text: tt("a.6478b238", "이대로면 {{v0}} {{v1}}", { v0: ttl(next), v1: ttl(dailyTier) }) }, right: base.settings.right, track: "track" },
        myTier: { ...base.myTier, badge: tt("a.6cd793b6", "{{v0}} 등급", { v0: ttl(monthText) }), right: todayLine(summary), chip: { tone: "light", text: tt("a.6478b238", "이대로면 {{v0}} {{v1}}", { v0: ttl(next), v1: ttl(dailyTier) }) }, msg: plain(keep ? tt("a.34ec1856", "여유 +{{v0}}점 — 컷이 움직이니 조금 더 벌어두세요", { v0: keep }) : tt("a.d74d8c43", "컷이 움직이니 조금 더 벌어두세요")), nextTitle: tt("a.62302382", "{{v0}} 굳히는 법", { v0: ttl(dailyTier) }) },
      };
    case "down":
      return {
        ...base,
        subline: [seg(tt("a.680e4e82", "오늘 기준") + " "), seg(dailyTier, true)],
        mid: { kind: "gauge", label: tt("a.7a6aeca8", "{{v0}} 유지까지", { v0: ttl(tier) }), value: keep ? tt("a.tier.pts", "+{{v0}}점", { v0: keep }) : "", pct: 0, marker: 0, left: dailyTier, right: nextTierName(tier) ? `${ttl(tier)} ── ${ttl(nextTierName(tier))}` : tier },
        chip: { tone: "dark", text: tt("a.f654729c", "지금 추이면 {{v0}} {{v1}}", { v0: ttl(next), v1: ttl(dailyTier) }) },
        coach: [seg(tt("a.401396b3", "{{v0}}을 지키려면", { v0: ttl(tier) }) + " "), seg(keep ? tt("a.c9d49ad6", "+{{v0}}점", { v0: keep }) : tt("a.24cf0dab", "조금 더"), true), seg(tt("a.864113ec", "이 필요해요. 가장 낮은 축부터 채우면 올라와요."))],
        ringPercent: 0,
        mobile: { line2: [seg(keep ? tt("a.537366f9", "지키려면 +{{v0}}점", { v0: keep }) : tt("a.6714a5b7", "지키려면 조금 더"), true), seg(" " + tt("a.b937a250", "· {{v0}} 결정", { v0: ttl(next) }))], badges: [{ kind: "dark", text: tt("a.9f4b2744", "오늘 {{v0}}", { v0: ttl(dailyTier) }) }] },
        tooltip: { title: tt("a.d1690f4a", "{{v0}} · 오늘 기준 {{v1}}", { v0: ttl(tier), v1: ttl(dailyTier) }), sub: keep ? tt("a.537366f9", "지키려면 +{{v0}}점", { v0: keep }) : tt("a.6714a5b7", "지키려면 조금 더") },
        settings: { badge: tt("a.6cd793b6", "{{v0}} 등급", { v0: ttl(monthText) }), sub: [seg(keep ? tt("a.537366f9", "지키려면 +{{v0}}점", { v0: keep }) : "")], chip: { tone: "dark", text: tt("a.f654729c", "지금 추이면 {{v0}} {{v1}}", { v0: ttl(next), v1: ttl(dailyTier) }) }, right: base.settings.right, track: "track" },
        myTier: { ...base.myTier, badge: tt("a.6cd793b6", "{{v0}} 등급", { v0: ttl(monthText) }), right: todayLine(summary), chip: { tone: "dark", text: tt("a.f654729c", "지금 추이면 {{v0}} {{v1}}", { v0: ttl(next), v1: ttl(dailyTier) }) }, msg: plain(tt("a.0f8f0b4a", "{{v0}}을 지키려면 {{v1}} — 가장 낮은 축부터", { v0: ttl(tier), v1: keep ? `+${keep}점` : "조금 더" })), nextTitle: tt("a.46de08f8", "{{v0}} 지키는 법", { v0: ttl(tier) }) },
      };
    case "top": {
      const rank = summary.bandRank && summary.bandSize ? `${ttl(tier)} ${summary.bandSize}명 중 ${summary.bandRank}위` : null;
      return {
        ...base,
        subline: rank ? [seg(tt("a.283982c6", "{{v0}} {{v1}}명 중", { v0: ttl(tier), v1: summary.bandSize }) + " "), seg(tt("a.11cfdf6d", "{{v0}}위", { v0: summary.bandRank }), true)] : null,
        mid: { kind: "plain", label: tt("a.b39722c6", "최고 등급"), value: keep ? tt("a.tier.holdHeadroom", "유지까지 여유 +{{v0}}점", { v0: keep }) : "", text: [seg(tt("a.62198721", "더 올라갈 등급이 없어요. 이제는 지키는 단계")), seg(keep ? " " + tt("a.7da68f64", "— {{v0}} 컷보다", { v0: tier }) + " " : ""), seg(keep ? tt("a.f2378517", "{{v0}}점 위", { v0: keep }) : "", true), seg(keep ? tt("a.e2a22830", "에 있어요.") : tt("a.e8fc3b11", "예요."))] },
        coach: plain(tt("a.38ae05d6", "등급은 {{v0}}에 다시 결정돼요. 휴면 없이 꾸준히 활동하면 지금 등급이 유지돼요.", { v0: ttl(next) })),
        ringPercent: 100,
        mobile: { line2: [seg(tt("a.f418178f", "최고 등급 · {{v0}}{{v1}} 결정", { v0: rank ? tt("a.tier.rankPrefix", "{{v0}}명 중 {{v1}}위 · ", { v0: summary.bandSize, v1: summary.bandRank }) : "", v1: next }))], badges: [] },
        tooltip: { title: rank ? tt("a.45ac0b17", "{{v0}} · {{v1}}명 중 {{v2}}위", { v0: tier, v1: summary.bandSize, v2: summary.bandRank }) : tier, sub: keep ? tt("a.b2ee6650", "유지까지 여유 +{{v0}}점", { v0: keep }) : tt("a.560aa9f9", "최고 등급 유지 중") },
        settings: { badge: tt("a.7e3c5527", "{{v0}} 등급 · 최고 등급", { v0: ttl(monthText) }), sub: [seg(`${rank ? `${rank} · ` : ""}${keep ? tt("a.tier.holdHeadroom", "유지까지 여유 +{{v0}}점", { v0: keep }) : tt("a.560aa9f9", "최고 등급 유지 중")}`)], right: base.settings.right, track: "track", trackValue: rank || "" },
        myTier: { ...base.myTier, badge: tt("a.7e3c5527", "{{v0}} 등급 · 최고 등급", { v0: ttl(monthText) }), right: todayLine(summary), msg: plain(tt("a.e2ff3109", "더 올라갈 등급이 없어요 — 이제는 지키는 단계예요")), pc2: rank ? `${rank}${keep ? ` · 컷보다 ${keep}점 위` : ""}` : "", nextTitle: tt("a.ba32c201", "{{v0}} 유지하는 법", { v0: ttl(tier) }) },
      };
    }
    case "stable":
    default:
      return {
        ...base,
        mid: gauge(summary, nextTier, gap),
        coach: plain(tt("a.cc016b6a", "오늘까지의 활동으로 {{v0}}에 등급이 결정돼요.", { v0: ttl(next) })),
        ringPercent: summary.progressPercent ?? 0,
        mobile: { line2: gapLine(nextTier, gap, next, summary), badges: [] },
        tooltip: { title: tier, sub: gapText(nextTier, gap, summary) },
        settings: { badge: tt("a.6cd793b6", "{{v0}} 등급", { v0: ttl(monthText) }), sub: plain(gapText(nextTier, gap, summary)), right: base.settings.right, track: "track" },
        myTier: { ...base.myTier, badge: tt("a.6cd793b6", "{{v0}} 등급", { v0: ttl(monthText) }), right: todayLine(summary), msg: plain(gapText(nextTier, gap, summary)) },
      };
  }
}

// 2026-09-18 — 등급제 "절대평가 전환"(v16): 마스터는 점수 컷이 아니라 전체 상위 N% 특수
// 등급이라(ALGORITHM-SPEC-v16-ABSOLUTE.md §3), "마스터까지 +N점" 같은 점수-격차 문구가 더 이상
// 사실이 아니다. nextTier가 마스터일 때는 아래 세 함수(gauge/gapText/gapLine) 전부 이 문구
// 대신 백분위 기반 문구로 분기한다 — 이 세 함수가 debut/promoted/stable 세 kind의 mid/tooltip/
// settings/mobile/myTier.msg 문구를 전부 만드는 단일 출처라, 여기 하나만 고치면 그 문구를 쓰는
// 화면(MyTier·Settings·TopNavBar·Home) 전부에 함께 반영된다.
// 2026-09-21 — v17 초안: 비율 10%→20%(`MASTER_TOP_RATIO`, ALGORITHM-SPEC-v17-DRAFT.md §0).
function masterPctText(summary) {
  const pct = summary && summary.percentileOverall;
  return pct !== null && pct !== undefined
    ? tt("a.tier.masterTopPct", "마스터는 상위 {{v1}}%에게 주어져요 — 현재 상위 {{v0}}%", { v0: pct, v1: MASTER_TOP_RATIO })
    : tt("a.tier.masterTopPctUnknown", "마스터는 상위 {{v0}}%에게 주어지는 특수 등급이에요", { v0: MASTER_TOP_RATIO });
}
function gauge(summary, nextTier, gap) {
  if (nextTier === MASTER_TIER_NAME) {
    return { kind: "plain", label: tt("a.tier.masterLabel", "마스터"), value: tt("a.tier.topRatio", "상위 {{v0}}%", { v0: MASTER_TOP_RATIO }), text: plain(masterPctText(summary)) };
  }
  return { kind: "gauge", label: tt("a.94cb2ff4", "{{v0}}까지", { v0: ttl(nextTier) }), value: gap ? tt("a.tier.pts", "+{{v0}}점", { v0: gap }) : "", pct: summary.progressPercent ?? 0, left: summary.primaryTier, right: nextTier };
}
function gapText(nextTier, gap, summary) {
  if (!nextTier) return tt("a.560aa9f9", "최고 등급 유지 중");
  if (nextTier === MASTER_TIER_NAME) return masterPctText(summary);
  return gap ? tt("a.0a0f8d01", "{{v0}}까지 +{{v1}}점", { v0: ttl(nextTier), v1: gap }) : tt("a.94cb2ff4", "{{v0}}까지", { v0: ttl(nextTier) });
}
function gapLine(nextTier, gap, next, summary) {
  if (!nextTier) return [seg(tt("a.21f9c7b8", "최고 등급 · {{v0}} 결정", { v0: ttl(next) }))];
  if (nextTier === MASTER_TIER_NAME) return [seg(masterPctText(summary))];
  return [seg(tt("a.94cb2ff4", "{{v0}}까지", { v0: ttl(nextTier) }) + " "), seg(gap ? tt("a.c9d49ad6", "+{{v0}}점", { v0: gap }) : "", true), seg(" " + tt("a.b937a250", "· {{v0}} 결정", { v0: ttl(next) }))];
}
// "9/1 기준 · 골드 · 적용보다 아래" — /my-tier 히어로 우측 문구.
function todayLine(summary) {
  const d = summary.dailyDate ? shortDate(summary.dailyDate) : null;
  const rel = summary.direction === "up" ? tt("a.57428efa", "적용보다 위") : summary.direction === "down" ? tt("a.29f29bac", "적용보다 아래") : tt("a.7e2895ba", "적용 중과 같음");
  const t = summary.dailyTier || summary.primaryTier;
  return [seg(d ? tt("a.f79d19f0", "{{v0}} 기준 ·", { v0: d }) + " " : ""), seg(ttl(t), true), seg(` · ${rel}`)];
}
function shortDate(dateStr) {
  const m = parseInt(dateStr.slice(5, 7), 10);
  const d = parseInt(dateStr.slice(8, 10), 10);
  return `${m}/${d}`;
}

// 세그먼트 → 평문(툴팁·aria 용).
export function segmentsToText(segs) {
  return (segs || []).map((s) => s.text).join("");
}

// 등급 순서 도우미 — 화면이 트랙을 그릴 때 쓴다.
export function tierOrderIndex(tierName) {
  return tierIndex(tierName);
}
export const TIER_ORDER = CREATOR_TIER_NAMES;
